
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface TargetHireDateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: string;
  currentTargetDate?: Date;
}

export const TargetHireDateDialog: React.FC<TargetHireDateDialogProps> = ({
  isOpen,
  onClose,
  jobId,
  currentTargetDate,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(currentTargetDate);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSave = async () => {
    if (!selectedDate) return;
    
    setIsUpdating(true);
    try {
      // TODO: Implement API call to update target hire date
      console.log(`Setting target hire date for job ${jobId} to:`, selectedDate);
      toast.success('Target hire date updated successfully');
      onClose();
    } catch (error) {
      console.error('Error updating target hire date:', error);
      toast.error('Failed to update target hire date');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Set Target Hire Date</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Target Date:</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !selectedDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {selectedDate ? format(selectedDate, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => date < new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={isUpdating || !selectedDate}
          >
            {isUpdating ? 'Saving...' : 'Save Target Date'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
